import { useTranslation } from "react-i18next";
import { Button } from '@/components/ui/button';
import { Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { applyExplicitUserLanguage } from '@/lib/languagePolicy';
import { SUPPORTED_LANGUAGES } from '@/lib/languages';
import type { SupportedLanguage } from '@/lib/languages';
import { cn } from '@/lib/utils';

export type LanguageSwitcherVariant = "light" | "dark";

export interface LanguageSwitcherProps {
  /** Light header (default) vs dark header (e.g. BaseTemplateBlack, legal pages). */
  variant?: LanguageSwitcherVariant;
  className?: string;
}

export function LanguageSwitcher({ variant = "light", className }: LanguageSwitcherProps = {}) {
  const { i18n } = useTranslation();

  const changeLanguage = (lng: SupportedLanguage) => {
    applyExplicitUserLanguage(i18n, lng);
  };

  const currentLang = SUPPORTED_LANGUAGES.find(lang => i18n.language.startsWith(lang.code)) || SUPPORTED_LANGUAGES[0];
  const isDark = variant === "dark";

  return (
    <div
      className={cn("flex items-center font-body", className)}
      data-testid="language-switcher"
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            aria-label="Change language"
            data-testid="language-switcher-trigger"
            className={cn(
              "h-9 w-9 shrink-0 p-0 gap-0 sm:h-9 sm:w-auto sm:px-3 sm:gap-2",
              isDark &&
                "text-white/90 hover:bg-white/10 hover:text-white focus-visible:ring-white/30",
            )}
          >
            <Globe className="h-4 w-4" />
            <span className="hidden text-xs sm:inline">{currentLang.flag}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className={cn(
            "font-body",
            isDark &&
              "border border-white/10 bg-neutral-950 text-white data-[state=open]:bg-neutral-950",
          )}
        >
          {SUPPORTED_LANGUAGES.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              data-testid={`language-option-${lang.code}`}
              onClick={() => changeLanguage(lang.code)}
              className={cn(
                "gap-2 cursor-pointer",
                isDark && "focus:bg-white/10 focus:text-white",
              )}
            >
              <span>{lang.flag}</span>
              <span>{lang.nativeName}</span>
              {i18n.language.startsWith(lang.code) && (
                <span className={cn("ml-auto", isDark ? "text-white" : "text-primary")}>✓</span>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
